import React from 'react';
import { navigationClickEvent } from 'analytics/events/studio';
import { Collapse } from 'react-collapse';
import {
  Button,
  Col,
  Divider,
  getTypographyDefaultColor,
  MinusIcon,
  PlusIcon,
  Row,
  Space,
  SpanConfig,
  Typography
} from '@ag1/ui';
import clsx from 'clsx';
import { BenefitsJourneyV4 } from 'types/studio/studio';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import styles from './BenefitsJourneyCardV4.module.scss';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { StudioBaseIconV2 } from '../StudioBaseIcon/StudioBaseIcon';
import CustomPortableText from '../PortableText/PortableText';

export type BenefitsJourneyCardV4Props = { sectionType: string } & Pick<
  BenefitsJourneyV4,
  'headline' | 'textAlignment' | 'theme'
> &
  BenefitsJourneyV4['cards'][number];

export const BenefitsJourneyCardV4 = ({
  sectionType,
  headline,
  image,
  mediaType,
  icon,
  subtitle,
  description,
  subtitle2,
  cardButton,
  expandedButton,
  expandedText,
  theme,
  textAlignment,
  visibleOnPageLoad
}: BenefitsJourneyCardV4Props & { visibleOnPageLoad: boolean }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onToggle = () => {
    if (!isOpen) {
      navigationClickEvent({
        header: headline,
        clickText: subtitle || '',
        navigationType: 'accordion',
        location: sectionType
      });
    }
    setIsOpen((s) => !s);
  };

  const textColConfig: SpanConfig =
    mediaType === 'icon' ? { xxs: 12, s: 10, l: 12 } : { xxs: 12, s: 6, l: 12 };
  return (
    <Row className={clsx(styles.item, mediaType === 'icon' && styles.itemsTop)}>
      {mediaType === 'image' && image && (
        <Col span={{ xxs: 12, s: 6, l: 12 }}>
          <StudioBaseImageV2
            image={image}
            aspectRatio={1.5}
            className={styles.imageContainer}
            childClassName={styles.img}
            sizes={{ xxs: 12, s: 6, l: 12 }}
            eagerLoading={visibleOnPageLoad}
          />
        </Col>
      )}
      {mediaType === 'icon' && icon && (
        <div
          className={clsx(
            styles.iconCont,
            textAlignment === 'left' && styles.left,
            textAlignment === 'center' && styles.center
          )}>
          <StudioBaseIconV2
            icon={icon}
            className={clsx(styles.icon, textAlignment === 'left' && styles.left)}
          />
        </div>
      )}

      <Col
        span={textColConfig}
        className={clsx(styles.text, textAlignment === 'left' && styles.left)}>
        {subtitle && (
          <h3
            className={clsx(
              styles.subtitle,
              mediaType === 'icon' && styles.marginTop,
              getThemeCSSClass(theme)
            )}>
            {subtitle}
          </h3>
        )}
        {description && (
          <Typography
            variant="body"
            size="1"
            className={styles.description}
            color={getTypographyDefaultColor(theme)}
            component="div"
            noPadding>
            <CustomPortableText addAccessibility value={description} />
          </Typography>
        )}
        {subtitle2 && (
          <Typography
            variant="body"
            size="1"
            component="h4"
            weight="700"
            noPadding
            color={getTypographyDefaultColor(theme)}
            className={styles.subtitle2}>
            {subtitle2}
          </Typography>
        )}
        <Collapse
          className={styles.collapse_css_transition}
          isOpened={isOpen}
          aria-hidden={isOpen ? 'false' : 'true'}>
          <div className={styles.expanded}>
            <Divider
              top={16}
              bottom={16}
              className={clsx(
                styles.divider,
                textAlignment === 'left' && styles.dividerLeft,
                mediaType === 'icon' && styles.dividerFull
              )}
            />
            {expandedText && (
              <CustomPortableText
                addAccessibility
                value={expandedText}
                className={styles.expandedText}
              />
            )}
          </div>
        </Collapse>
        <Space top={{ xxs: 8, l: 16 }}>
          {cardButton && (
            <Button
              variant="link"
              size="medium"
              theme={theme}
              icon={isOpen ? <MinusIcon /> : <PlusIcon />}
              iconPosition="right"
              onClick={onToggle}
              data-testid={`${sectionType}-card`}>
              {isOpen ? expandedButton : cardButton}
            </Button>
          )}
        </Space>
      </Col>
    </Row>
  );
};
